import { Badge, cn } from '@today/ui-primitives'
import type { ReactNode } from 'react'

/** One entry of the console's section navigation, already filtered by the server's manifest. */
export interface TenantConfigNavItem {
	key: string
	title: string
	/** True when the caller may view but not change the section. */
	readOnly: boolean
}

/**
 * Props for {@link TenantConfigConsoleView}.
 *
 * @property tenantName - The configured tenant, shown in the header.
 * @property planeLabel - Which console this is (e.g. "Platform administration" / "Tenant administration").
 * @property sections - The visible sections, in display order. Nothing else is rendered.
 * @property activeKey - The section whose content is shown.
 * @property onSelect - Called with a section key when the user picks it.
 * @property children - The active section's content.
 */
export interface TenantConfigConsoleViewProps {
	tenantName: string
	planeLabel: string
	sections: TenantConfigNavItem[]
	activeKey: string
	onSelect: (key: string) => void
	children: ReactNode
}

/**
 * The tenant configuration console frame (TO-374): a header, the section
 * navigation and the active section's content. Pure — the sections, their
 * access and the content arrive as props; the server decides what is visible.
 */
export function TenantConfigConsoleView({
	tenantName,
	planeLabel,
	sections,
	activeKey,
	onSelect,
	children,
}: TenantConfigConsoleViewProps) {
	return (
		<div className="flex flex-col gap-6 p-6">
			<header className="flex flex-col gap-1">
				<p className="text-ds-sm text-ds-text-muted">{planeLabel}</p>
				<h1 className="text-ds-xl font-semibold text-ds-text">{tenantName} — configuration</h1>
			</header>
			<div className="flex gap-6">
				<nav aria-label="Configuration sections" className="w-56 shrink-0">
					<ul className="flex flex-col gap-1">
						{sections.map((section) => {
							const active = section.key === activeKey
							return (
								<li key={section.key}>
									<button
										type="button"
										aria-current={active ? 'page' : undefined}
										onClick={() => onSelect(section.key)}
										className={cn(
											'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-ds-sm',
											active ? 'bg-ds-surface-muted font-semibold text-ds-text' : 'text-ds-text-muted',
										)}
									>
										<span>{section.title}</span>
										{section.readOnly ? (
											<Badge variant="muted" size="sm">
												Read-only
											</Badge>
										) : null}
									</button>
								</li>
							)
						})}
					</ul>
				</nav>
				<section className="min-w-0 flex-1">{children}</section>
			</div>
		</div>
	)
}

/** Props for {@link TenantConfigSectionPlaceholder}. */
export interface TenantConfigSectionPlaceholderProps {
	title: string
}

/** Shown for a section whose owning story has not registered its content yet. */
export function TenantConfigSectionPlaceholder({ title }: TenantConfigSectionPlaceholderProps) {
	return (
		<div className="flex flex-col gap-2 rounded-md border border-ds-border p-6">
			<h2 className="text-ds-base font-semibold text-ds-text">{title}</h2>
			<p className="text-ds-sm text-ds-text-muted">This section is not available yet</p>
		</div>
	)
}
